import { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import Pill from '../components/Pill';
import ResponseCard from '../components/ResponseCard';
import ModeToggle from '../components/ModeToggle';
import { generateExcuses, fetchStats, submitExcuse } from '../lib/api';
import type { GeneratedExcuse, Stats, RefineStyle } from '../lib/api';

const CATEGORIES = ['Work', 'Social', 'Family', 'Dating', 'Errands', 'Other'];

const PLACEHOLDERS: Record<string, string> = {
  Work: 'My manager scheduled a 6pm "team alignment" call on a Friday and wants everyone on camera...',
  Social: 'My friend group chat organised a last-minute dinner I definitely said maybe to...',
  Family: "There's a family gathering I agreed to three months ago and now regret...",
  Dating: 'I matched with someone who wants to go mini-golfing as a first date...',
  Errands: "I promised I'd help someone move flats this Saturday...",
  Other: "Describe the situation you're trying to escape...",
};

const SAMPLE_EXCUSES: GeneratedExcuse[] = [
  {
    type: 'THE SOFT BOUNDARY',
    text: "I won't be able to make it — I have a conflict that evening involving my couch, a blanket, and a prior commitment to doing absolutely nothing. I should have flagged this sooner. The commitment is non-negotiable.",
  },
  {
    type: 'THE PERSONAL TRUTH',
    text: "Genuinely cannot attend. I've done the math and the version of me that shows up to 6pm Friday calls is a lesser version that nobody benefits from. This is actually for everyone's good.",
  },
  {
    type: 'THE CHAOS CARD',
    text: "Something has come up. I can't explain it yet but I will be able to by Monday. I'm fine. Everything is fine. Please start without me and also without expecting me.",
  },
];

const REFINE_OPTIONS: { key: RefineStyle; label: string }[] = [
  { key: 'shorter', label: 'Shorter' },
  { key: 'warmer', label: 'Warmer' },
  { key: 'dramatic', label: 'More dramatic' },
];

export default function Home() {
  const [category, setCategory] = useState('Work');
  const [situation, setSituation] = useState('');
  const [excuses, setExcuses] = useState<GeneratedExcuse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
  }, []);

  const generate = async () => {
    if (!situation.trim()) return;
    setLoading(true);
    setError(null);
    setExcuses(null);
    try {
      const result = await generateExcuses(category, situation);
      setExcuses(result);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : 'Could not generate right now. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitToHof = async (text: string, type: string) => {
    try {
      const result = await submitExcuse({ text, type, category });
      if (result.status === 'approved') setToast("Submitted. It's on the Hall of Fame.");
      else if (result.status === 'pending') setToast('Submitted. Under review.');
      else setToast("Couldn't submit that one. Try rewriting it.");
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Submit failed.');
    }
    setTimeout(() => setToast(null), 3200);
  };

  const displayExcuses = excuses ?? (loading ? null : SAMPLE_EXCUSES);

  return (
    <div>
      <section className="pad-x" style={{ padding: '64px 40px 60px', maxWidth: 780 }}>
        <ModeToggle />
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.14em',
            color: 'var(--tag)',
            marginBottom: 24,
            textTransform: 'uppercase',
          }}
        >
          Honest. Usable. Slightly too specific.
        </div>
        <h1
          className="hero-title"
          style={{
            fontSize: 52,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
          }}
        >
          Real excuses for things
        </h1>
        <h1
          className="hero-title"
          style={{
            fontSize: 52,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            color: '#4a4744',
            marginBottom: 28,
          }}
        >
          you never agreed to.
        </h1>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--fg-muted)',
            maxWidth: 420,
            textWrap: 'pretty',
          }}
        >
          No lies. No guilt spiral. Just a well-worded reason to not be there —
          delivered with the exact level of regret the situation deserves.
        </p>
      </section>

      <div style={{ height: 1, background: 'var(--border)' }} />

      <section
        className="pad-x two-col"
        style={{
          padding: '48px 40px 80px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 48,
          maxWidth: 1100,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div>
            <Label>What are you escaping?</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {CATEGORIES.map((c) => (
                <Pill
                  key={c}
                  label={c}
                  active={category === c}
                  onClick={() => setCategory(c)}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>Describe the situation</Label>
            <textarea
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              placeholder={PLACEHOLDERS[category]}
              maxLength={500}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate();
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#555')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              style={{
                width: '100%',
                minHeight: 110,
                padding: '14px 16px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--fg)',
                fontFamily: 'inherit',
                fontSize: 14,
                lineHeight: 1.6,
                resize: 'vertical',
                transition: 'border-color 0.15s',
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 6 }}>
              {situation.length}/500 · ⌘↵ to generate
            </div>
          </div>

          <button
            type="button"
            onClick={generate}
            disabled={loading || !situation.trim()}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: loading || !situation.trim() ? '#2a2828' : 'var(--accent)',
              color: loading || !situation.trim() ? '#555' : '#0f0f0f',
              border: 'none',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 600,
              cursor: loading || !situation.trim() ? 'default' : 'pointer',
              transition: 'background 0.2s',
              letterSpacing: '-0.01em',
            }}
          >
            {loading ? 'Generating your excuses…' : 'Generate my excuses →'}
          </button>

          {error && <Notice tone="err">{error}</Notice>}
          {toast && <Notice tone="info">{toast}</Notice>}
        </div>

        <div ref={resultsRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading
            ? [0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="skeleton"
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '22px 24px',
                    height: 140,
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))
            : displayExcuses?.map((ex, i) => (
                <ResponseCard
                  key={`${ex.type}-${i}`}
                  item={ex}
                  refineOptions={REFINE_OPTIONS}
                  canSubmit={!!excuses}
                  onSubmit={handleSubmitToHof}
                />
              ))}
        </div>
      </section>

      <footer
        className="pad-x"
        style={{
          borderTop: '1px solid var(--border)',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--tag)' }}>
          <span style={{ fontWeight: 600, color: '#b8b4af' }}>
            {stats?.obligations_dodged?.toLocaleString() ?? '—'}
          </span>{' '}
          obligations tactfully dodged
        </span>
        <span style={{ fontSize: 13, color: 'var(--tag)', fontStyle: 'italic' }}>
          "You're not flaky. You're just honest about your limits."
        </span>
      </footer>
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.12em',
        color: 'var(--tag)',
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function Notice({ tone, children }: { tone: 'err' | 'info'; children: ReactNode }) {
  const c =
    tone === 'err'
      ? { bg: '#2a1a1a', border: '#5c2a2a', fg: '#e29090' }
      : { bg: 'var(--card-bg)', border: 'var(--border)', fg: 'var(--fg)' };
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.fg,
        padding: '10px 14px',
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
